import type { DBSchema, IDBPDatabase } from 'idb';
import { openStore, openWithin, reportOpenFailure } from './storeOpen.js';

/**
 * A last-known-good cache for whole REST responses, keyed by request.
 *
 * `Replica` (replica.ts) caches *fields* — one row per `{entity}:{id}:{field}`
 * — which is the right shape for field-mode entities reached through the
 * generic record endpoint. It is the wrong shape for webledger, whose reads
 * are denormalised document queries: a transaction list already joined to its
 * splits, an account tree, a currency table. Those have no field keys, come
 * from bespoke endpoints (ADR-0021 keeps document-group traffic on REST), and
 * a list has no record id at all. Rebuilding them out of field snapshots
 * would mean reimplementing the joins client-side.
 *
 * So this stores the response as it arrived. Same stance as `Replica`, and
 * worth repeating because it is what makes eviction harmless: **cache only,
 * never system of record.** Losing it costs a refetch, nothing more. The only
 * genuinely unrecoverable client-side data is the unsent outbox (queue.ts),
 * which is a separate database on purpose.
 *
 * Deliberately not a Service Worker. That would also make a *cold* start work
 * offline, which this does not — it only keeps an already-open tab useful
 * across a drop. A Service Worker owns the app shell's cache lifetime and
 * update story, which is its own decision to make, not a side effect of
 * fixing "I can't open a transaction on the train".
 */

interface CachedDoc {
  key: string;
  value: unknown;
  cachedAt: string;
}

interface DocCacheSchema extends DBSchema {
  docs: {
    key: string;
    value: CachedDoc;
  };
}

export interface CachedEntry<T> {
  value: T;
  /** ISO-8601 — the UI says how stale it is rather than pretending it is live. */
  cachedAt: string;
}

export interface DocCacheOptions {
  appId: string;
  env: 'prod' | 'test';
}

export class DocCache {
  private db: IDBPDatabase<DocCacheSchema> | undefined;
  /**
   * Mirrored in memory so a read is synchronous where it needs to be, and so
   * the cache still works for the rest of a session if IndexedDB is
   * unavailable (private windows, storage disabled). A memory-only cache is
   * strictly better than none.
   */
  private readonly memory = new Map<string, CachedEntry<unknown>>();

  constructor(private readonly opts: DocCacheOptions) {}

  async open(opts: { waitAtMostMs?: number } = {}): Promise<void> {
    const dbName = `colplat-doccache-${this.opts.appId}-${this.opts.env}`;
    try {
      const opening = openStore<DocCacheSchema>(dbName, 1, (db) => { db.createObjectStore('docs', { keyPath: 'key' }); });
      // Slow to open — held up by another tab — and this carries on memory-only,
      // as it already does when storage refuses it (2026-10-21).
      const db = opts.waitAtMostMs === undefined ? await opening : await openWithin(opening, opts.waitAtMostMs, dbName);
      if (!db) {
        void opening.then((late) => late.close()).catch(() => undefined);
        return;
      }
      this.db = db;
      for (const doc of await this.db.getAll('docs')) {
        this.memory.set(doc.key, { value: doc.value, cachedAt: doc.cachedAt });
      }
    } catch (err) {
      // Storage refused us. Carry on memory-only rather than failing the app
      // — and say so, because a refusal can mean this page's code is older
      // than what is stored (`reportOpenFailure`).
      reportOpenFailure(`colplat-doccache-${this.opts.appId}-${this.opts.env}`, err);
      this.db = undefined;
    }
  }

  get<T>(key: string): CachedEntry<T> | undefined {
    return this.memory.get(key) as CachedEntry<T> | undefined;
  }

  /** Every key under a prefix — for a caller that prunes entries whose record no longer exists. */
  keysUnder(prefix: string): string[] {
    return [...this.memory.keys()].filter((key) => key.startsWith(prefix));
  }

  async delete(key: string): Promise<void> {
    this.memory.delete(key);
    if (!this.db) return;
    try {
      await this.db.delete('docs', key);
    } catch {
      // See `set` — the memory mirror is already correct either way.
    }
  }

  async set(key: string, value: unknown): Promise<void> {
    const cachedAt = new Date().toISOString();
    this.memory.set(key, { value, cachedAt });
    if (!this.db) return;
    try {
      await this.db.put('docs', { key, value, cachedAt });
    } catch {
      // Quota, eviction, a private window — the memory mirror still stands.
    }
  }

  async clear(): Promise<void> {
    this.memory.clear();
    if (!this.db) return;
    try {
      await this.db.clear('docs');
    } catch {
      // As above.
    }
  }
}

